import type { LDClient, LDContext } from 'launchdarkly-js-client-sdk';
import { isEqual } from 'lodash-es';
import { datadogLogs } from '@datadog/browser-logs';
import type { User } from '@/types/users';
import type { Org } from '@/types/org';
import type { Team } from '@/types/teams';
import { getSportTypeKey } from '@/types/teams';
import { getMeQueryOptions } from '@/composables/useMeQuery';

let client: LDClient | undefined = undefined;
let currentContext: LDContext | undefined = undefined;

/**
 * Registers the single LaunchDarkly client instance (created once by the LDPlugin in main.ts)
 * so it can be read synchronously outside the Vue lifecycle, e.g. in router guards or plain modules.
 */
export function setLDClient(ldClient: LDClient): void {
  client = ldClient;
}

/**
 * Synchronously reads a flag from the same client instance `useLDFlag` reads from, so the two
 * can never disagree. Use in router guards and plain modules where hooks aren't available.
 */
export function getFlag<T>(flagKey: string, defaultValue: T): T {
  if (!client) return defaultValue;
  return client.variation(flagKey, defaultValue) as T;
}

export interface BuildContextParams {
  user?: User | null;
  org?: Org | null;
  teams?: Team[] | null;
  teamTwrn?: string | null;
}

export function buildLDContext(params: BuildContextParams): LDContext {
  const { user, org, teams, teamTwrn } = params;

  const context: LDContext = {
    kind: 'multi',
  };

  if (user) {
    context.user = {
      key: user.twrn,
      name: user.fullName,
      email: user.email,
    };
  }

  if (org) {
    context.org = {
      key: org.twrn,
      name: org.name,
    };
  }

  if (teamTwrn && teams) {
    const currentTeam = teams.find((team) => team.twrn === teamTwrn);
    if (currentTeam) {
      context.team = {
        key: currentTeam.twrn,
        name: currentTeam.name,
        sport_type: getSportTypeKey(currentTeam.sportType),
      };
    }
  }

  return context;
}

async function getContextParams(): Promise<BuildContextParams | null> {
  const { useMainStore } = await import('@/stores/mainStore');
  const { queryClient } = await import('@/plugins/vueQuery');

  const mainStore = useMainStore();

  const me = await queryClient.query(getMeQueryOptions());

  return {
    user: me?.user,
    org: me?.org,
    teams: me?.teams,
    teamTwrn: mainStore.teamTwrn,
  };
}

let identifyPromise: Promise<LDClient | undefined> | null = null;
let hasQueuedUpdate = false;

async function performUpdateLDContext(): Promise<LDClient | undefined> {
  if (!client) {
    console.warn('[LaunchDarkly] No client has been set; call setLDClient() first');
    return undefined;
  }

  const params = await getContextParams();

  if (!params) {
    console.warn('[LaunchDarkly] User context missing');
    return undefined;
  }

  const newContext = buildLDContext(params);

  if (isEqual(currentContext, newContext)) {
    return client;
  }

  // Only record the new context as "current" once identify() has actually succeeded,
  // so a rejected call doesn't get memoized and skipped on the next retry.
  await client.identify(newContext);
  currentContext = newContext;

  return client;
}

/**
 * Rebuilds the LD context from the current user/team and identifies the borrowed client
 * if it's changed. Concurrent calls (e.g. App.vue's teamTwrn watcher firing at the same
 * time a router guard awaits this before checking requiredLDFlags) share a single
 * in-flight call rather than racing two separate identify() calls — and, more
 * importantly, this lets a guard safely await the *current* identification to settle
 * before reading flags, instead of getFlag() reading a stale, not-yet-updated context.
 *
 * If another call arrives while identify is in flight (e.g. a team switch mid-identify),
 * one follow-up update is queued and run after the in-flight call settles so the final
 * context reflects the latest teamTwrn. The promise is cleared once settled so the next
 * call always re-evaluates the context from scratch.
 */
export function updateLDContext(): Promise<LDClient | undefined> {
  if (identifyPromise) {
    hasQueuedUpdate = true;
    return identifyPromise;
  }

  identifyPromise = (async () => {
    let result: LDClient | undefined;
    let lastError: unknown;
    do {
      hasQueuedUpdate = false;
      lastError = undefined;
      try {
        result = await performUpdateLDContext();
      } catch (error) {
        lastError = error;
        datadogLogs.logger.error('[LaunchDarkly] Failed to update context', {
          error: error instanceof Error
            ? {
                message: error.message,
                stack: error.stack,
              }
            : String(error),
        });
      }
    } while (hasQueuedUpdate);
    if (lastError) throw lastError;
    return result;
  })().finally(() => {
    identifyPromise = null;
  });

  return identifyPromise;
}

/**
 * Subscribes to the borrowed client's 'change' event, which fires whenever any flag's
 * value changes via LD's streaming connection. Used to react to a flag being toggled
 * while the user is already on a page that depends on it, rather than only catching it
 * on the next navigation. Returns an unsubscribe function; no-ops if no client has been
 * set yet.
 */
export function onFlagsChange(callback: () => void): () => void {
  if (!client) return () => {};

  const ldClient = client;
  const listener = () => callback();
  ldClient.on('change', listener);
  return () => ldClient.off('change', listener);
}

export async function checkLDFlags(flagKeys: string[]): Promise<boolean> {
  const ldClient = await updateLDContext();
  if (!ldClient) return false;

  for (const flag of flagKeys) {
    if (ldClient.variation(flag, false) === false) {
      return false;
    }
  }
  return true;
}
